import AWS from 'aws-sdk';
import https from 'https';
import { DynamoDbPolicy } from '@ourbranch/schema-types/dynamodb';

const sslAgent = new https.Agent({ keepAlive: true, maxSockets: 50, rejectUnauthorized: true });
const docClient = new AWS.DynamoDB.DocumentClient({
  apiVersion: '2012-08-10',
  region: 'us-east-1',
  maxRetries: 20,
  httpOptions: { agent: sslAgent }
});

const POLICY_TABLE = 'policy';
const POLICY_AUDIT_TABLE = 'policyaudit';
const POLICY_OFFER_ID_INDEX = 'offerId';

/**
 * Fetch a policy by its policy ID.
 *
 * The accountId (DynamoDB partition key) is derived from the policyId — it's the
 * segment before the first hyphen (e.g. "12345678-TX-001" → accountId "12345678").
 *
 * Table names are hardcoded — they are the same across all environments:
 * - policy table: 'policy'
 * - extended data table: 'policyextendeddata' (used after hydration is wired in)
 */
export function getPolicy(policyId: string): Promise<DynamoDbPolicy | undefined> {
  const accountId = policyId.split('-')[0];
  return new Promise((resolve, reject) => {
    docClient
      .get({ TableName: POLICY_TABLE, Key: { accountId, id: policyId } })
      .promise()
      .then((data) => resolve(data.Item as DynamoDbPolicy | undefined))
      .catch((err) => {
        console.error(err, err.stack);
        reject(err);
      });
  });
}

/**
 * Fetch a policy associated with an offer ID.
 *
 * Uses the `offerId` GSI on the policy table (HASH key only — no sort key).
 * A single offer can produce multiple policies (e.g. both auto and home from
 * the same bundle offer), so the returned item is indeterminate when more than
 * one policy shares the given offerId. Limit: 1 is set for efficiency.
 *
 * Returns undefined if no policy is associated with the given offer.
 */
export function getPolicyByOfferId(offerId: string): Promise<DynamoDbPolicy | undefined> {
  return docClient
    .query({
      TableName: POLICY_TABLE,
      IndexName: POLICY_OFFER_ID_INDEX,
      KeyConditionExpression: 'offerId = :offerId',
      ExpressionAttributeValues: { ':offerId': offerId },
      Limit: 1
    })
    .promise()
    .then((data) => data.Items?.[0] as DynamoDbPolicy | undefined);
}

/**
 * Save a policy to the policy table and write the old version to the audit table.
 *
 * All three operations are atomic via DynamoDB TransactWrite:
 * 1. Delete the old policy record (with version condition — fails if a concurrent write occurred)
 * 2. Put the new policy record
 * 3. Put the old policy record to policyaudit
 *
 * Table names are hardcoded — they are the same across all environments:
 * - policy table: 'policy'
 * - audit table: 'policyaudit'
 */
export function savePolicy(newPolicy: DynamoDbPolicy, oldPolicy: DynamoDbPolicy): Promise<void> {
  if (newPolicy.id !== oldPolicy.id) {
    return Promise.reject(
      new Error(`savePolicy: policy ID mismatch — newPolicy.id "${newPolicy.id}" !== oldPolicy.id "${oldPolicy.id}"`)
    );
  }
  const accountId = newPolicy.id.split('-')[0];
  return docClient
    .transactWrite({
      TransactItems: [
        {
          Delete: {
            TableName: POLICY_TABLE,
            Key: { accountId, id: newPolicy.id },
            ConditionExpression: 'version = :v',
            ExpressionAttributeValues: { ':v': oldPolicy.version }
          }
        },
        {
          Put: {
            TableName: POLICY_TABLE,
            Item: newPolicy
          }
        },
        {
          Put: {
            TableName: POLICY_AUDIT_TABLE,
            Item: oldPolicy
          }
        }
      ]
    })
    .promise()
    .then(() => undefined);
}
